import { ChangeDetectionStrategy, AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {RequestParamModel} from "@coremodels";
import {CommonSearchService} from "@coreapiservices";
import {Router} from "@angular/router";

/** The five record kinds `common-search` can return, as short slugs used for icons/accents. */
type SearchType = 'account' | 'contact' | 'sample' | 'quote' | 'order';
type Scope = SearchType | 'all';

/** One title run, split so the matched part can be rendered inside <mark>. */
interface TitleSegment {
  text: string;
  hit: boolean;
}

/** A `common-search` row normalised for display. */
interface SearchHit {
  id: number;
  type: SearchType;
  title: string;
  segments: TitleSegment[];
  subtitle: string;
  ref: string;
  link: any[];
}

interface HeaderRow {
  kind: 'header';
  label: string;
}

interface HitRow {
  kind: 'hit';
  index: number;
  hit: SearchHit;
}

type Row = HeaderRow | HitRow;

/** module string returned by the API → slug + group heading + route prefix. */
const MODULES: { module: string; type: SearchType; label: string; route: string }[] = [
  {module: 'Account', type: 'account', label: 'Accounts', route: '/accounts'},
  {module: 'Contact', type: 'contact', label: 'Contacts', route: '/contacts'},
  {module: 'Sample Request', type: 'sample', label: 'Sample Requests', route: '/sample-requests/view'},
  {module: 'Quote', type: 'quote', label: 'Quotes', route: '/quotes/view'},
  {module: 'Order', type: 'order', label: 'Orders', route: '/orders/view'}
];

/** Rows rendered per scroll step. Keeps the DOM small without costing a request. */
const RENDER_WINDOW = 50;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-console-search-dialog',
  templateUrl: './console-search-dialog.component.html',
  styleUrls: ['./console-search-dialog.component.css']
})

export class ConsoleSearchDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('input') input?: ElementRef<HTMLInputElement>;
  @ViewChild('scopeBar') scopeBar?: ElementRef<HTMLElement>;

  term = '';
  scope: Scope = 'all';
  activeIndex = 0;
  iskeywordSearchLoading = false;
  /** True once a request for the current term has come back — drives the "no matches" state. */
  searched = false;

  /** Every hit loaded for the current term, in API order. */
  hits: SearchHit[] = [];
  /** Header + hit rows for the active scope, flattened for single-pass render and keyboard nav. */
  rows: Row[] = [];
  /** Hits currently rendered — what the keyboard can reach. */
  hitCount = 0;
  /** Hits matching the active scope in total, rendered or not. Drives the scroll guard. */
  scopeHitTotal = 0;
  counts: Record<Scope, number> = {all: 0, account: 0, contact: 0, sample: 0, quote: 0, order: 0};

  /**
   * Tail of the empty-state hint — " — 26,070 records indexed" — or '' until the count
   * lands, so the sentence reads correctly either way (the full stop is in the
   * template). Built here rather than in the template so the spacing around the em
   * dash is not at the mercy of Angular's whitespace collapsing.
   */
  indexNote = '';

  readonly scopes: { key: Scope; label: string }[] = [
    {key: 'all', label: 'All'},
    {key: 'account', label: 'Accounts'},
    {key: 'contact', label: 'Contacts'},
    {key: 'sample', label: 'Samples'},
    {key: 'quote', label: 'Quotes'},
    {key: 'order', label: 'Orders'}
  ];

  searchKeyword: any;
  /**
   * The whole match set for the term, in one request.
   *
   * This used to be `pageSize = 50` with page-by-page infinite scroll, and that is what made the
   * scope counts wrong: they are tallied from the rows in hand, so a term matching more than one
   * page reported the PAGE SIZE as its count — `All 50 / Orders 50` — and every module that sorts
   * after the first 50 rows (the API merges accounts, then contacts, then quotes/orders) showed no
   * badge at all. The badges also grew as you scrolled, because each appended page re-tallied them.
   *
   * `all` is honoured by this endpoint: `CommonSearch::search()` passes its own `pageSize` straight
   * into the ArrayDataProvider's pagination config, and `Pagination::setPageSize()` only clamps to
   * `pageSizeLimit` for values that came from the `per-page` REQUEST param, so nothing caps it at
   * 50; `'all'` casts to 0, and ArrayDataProvider skips its `array_slice()` when the page size is
   * not > 0. The server cost is unchanged either way — the model already runs all three queries
   * unbounded and merges them in PHP before paginating.
   */
  readonly pageSize = 'all';
  /** How many hits of the active scope are rendered. Grows on scroll — no further requests. */
  visibleCount = RENDER_WINDOW;
  requestParamModel!: RequestParamModel;

  private debounceTimer: any;

  constructor(
    public activeModal: NgbActiveModal,
    private changeDetection: ChangeDetectorRef,
    private _service: CommonSearchService,
    private router: Router
  ) {
  }

  ngAfterViewInit() {
    setTimeout(() => this.input?.nativeElement.focus(), 30);
    // Cached on the service, so this is one request per session, not per open.
    // OnPush + an HTTP response that schedules no tick of its own — same reason the
    // search path repaints by hand.
    this._service.indexSize().subscribe(total => {
      if (total > 0) {
        this.indexNote = ` - ${total.toLocaleString('en-AU')} record${total === 1 ? '' : 's'} indexed`;
        this.changeDetection.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    clearTimeout(this.debounceTimer);
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    this.activeIndex = 0;
    this.visibleCount = RENDER_WINDOW;
    if (!this.term.trim()) {
      this.searchCleared();
      return;
    }
    this.debounceTimer = setTimeout(() => this.search(this.term.trim()), 600);
  }

  clear() {
    this.term = '';
    this.searchCleared();
    this.input?.nativeElement.focus();
  }

  setScope(scope: Scope) {
    this.scope = scope;
    this.activeIndex = 0;
    this.visibleCount = RENDER_WINDOW;
    this.buildRows();
    this.input?.nativeElement.focus();
    this.revealScope(scope);
  }

  search($event: any) {
    if (!$event) {
      this.searchCleared();
    } else {
      this.searchKeyword = $event;
      this.iskeywordSearchLoading = true;
      this.changeDetection.detectChanges();
      this.requestParamModel = new RequestParamModel();
      this.requestParamModel.addDefaultFilter('search_keyword', encodeURIComponent($event), 'string');
      this.requestParamModel.pageSize = this.pageSize;
      this._service.getSearchResult(this.requestParamModel, true).subscribe((dataList: any) => {
        this.requestParamModel.update<any>(dataList);
        this.hits = (dataList.data || []).map((item: any) => this.toHit(item));

        // Filter out duplicate objects based on the id property
        this.hits = this.hits.filter((item, index, self) =>
          index === self.findIndex((t) => (
            t.id === item.id && t.type === item.type
          ))
        );

        this.searched = true;
        this.buildRows();
        // clear BEFORE detectChanges: the view is OnPush and an HTTP response does not
        // schedule a tick of its own, so a flag flipped afterwards never repaints and
        // the inline spinner would stay on screen for good.
        this.iskeywordSearchLoading = false;
        this.changeDetection.detectChanges();
      }, (error: HttpErrorResponse) => {
        this.iskeywordSearchLoading = false;
        this.searched = true;
        this.changeDetection.detectChanges();
      });
    }
  }

  searchCleared() {
    this.hits = [];
    this.searched = false;
    this.visibleCount = RENDER_WINDOW;
    this.buildRows();
    this.changeDetection.detectChanges();
  }

  /**
   * Infinite scroll — reveal the next slice once the list is near its end. The whole match set is
   * already in hand (see `pageSize`), so this widens the render window instead of fetching, which
   * is also what keeps the scope counts still: they describe the match set, not what is rendered.
   */
  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 48) {
      return;
    }
    if (this.visibleCount >= this.scopeHitTotal) {
      return;
    }
    this.visibleCount += RENDER_WINDOW;
    this.buildRows();
    this.changeDetection.detectChanges();
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        if (this.hitCount) {
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.hitCount;
          this.scrollActiveIntoView();
        }
        break;
      case 'ArrowUp':
        if (this.hitCount) {
          event.preventDefault();
          this.activeIndex = (this.activeIndex - 1 + this.hitCount) % this.hitCount;
          this.scrollActiveIntoView();
        }
        break;
      case 'Enter': {
        const active = this.rows.find(row => row.kind === 'hit' && row.index === this.activeIndex);
        if (active && active.kind === 'hit') {
          this.selectEvent(active.hit);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  selectEvent(item: SearchHit) {
    if (item) {
      this.hits = [];
      this.activeModal.close();
      // Only release the trigger once the route has actually changed — a
      // cancelled navigation leaves the user where they were, and there the
      // restored focus is the correct behaviour.
      this.router.navigate(item.link).then(navigated => {
        if (navigated) {
          this.releaseTriggerFocus();
        }
      });
    }
  }

  /**
   * ng-bootstrap refocuses whatever opened the modal when it closes
   * (`NgbModalWindow._restoreFocus`) — here the header's search button. That is
   * right when the palette is dismissed with Escape or the backdrop: the route
   * is unchanged, so a keyboard user belongs back where they started. After a
   * hit is opened it is wrong. The trigger regains `:focus-visible`, which
   * `html .tip:focus-visible::after` (styles.css) reveals as its tooltip, so the
   * destination page loads with the Search control lit and captioned — still
   * reading as the current context long after the route moved on.
   *
   * This only ever *releases* focus. The console has no `<main>` landmark to
   * move it to, so focus falls to <body> — exactly where clicking an ordinary
   * nav link leaves it. Scoped to the header so a destination page that
   * autofocuses something of its own is never blurred out from under itself.
   */
  private releaseTriggerFocus(): void {
    // Queued behind ng-bootstrap's own restore timer: it schedules a 0ms
    // setTimeout while the window hides, this one is scheduled after, and equal
    // delays fire in order — so the blur lands on the refocused trigger rather
    // than racing it.
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body && active.closest('app-header')) {
        active.blur();
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  /** Regroup the hits for the active scope and re-flatten them into rows. */
  private buildRows(): void {
    // Counts describe the whole match set, never the render window — that is the difference
    // between a scope badge and a "rows on screen" tally, and console-v2 shows the former.
    const counts: Record<Scope, number> = {all: 0, account: 0, contact: 0, sample: 0, quote: 0, order: 0};
    for (const hit of this.hits) {
      counts[hit.type]++;
      counts.all++;
    }
    this.counts = counts;
    this.scopeHitTotal = this.scope === 'all' ? counts.all : counts[this.scope];

    const rows: Row[] = [];
    let index = 0;
    for (const module of MODULES) {
      if (this.scope !== 'all' && this.scope !== module.type) {
        continue;
      }
      const group = this.hits.filter(hit => hit.type === module.type);
      if (!group.length || index >= this.visibleCount) {
        continue;
      }
      rows.push({kind: 'header', label: module.label});
      for (const hit of group.slice(0, this.visibleCount - index)) {
        rows.push({kind: 'hit', index: index++, hit});
      }
    }
    this.rows = rows;
    this.hitCount = index;
    if (this.activeIndex >= this.hitCount) {
      this.activeIndex = 0;
    }
  }

  /**
   * Normalise a `common-search` row for display. The API returns `title`, `sub_title` and
   * `module`; quotes/orders/sample requests arrive as "REF: Project name", and accounts carry
   * their ABN in `sub_title`. Anything the API supplies explicitly (`ref` / `subtitle`) wins,
   * so richer payloads render without a frontend change.
   */
  private toHit(item: any): SearchHit {
    const module = MODULES.find(m => m.module === item.module) || MODULES[0];
    let title = (item.title || '').trim();
    let subtitle = (item.subtitle || '').trim();
    let ref = (item.ref || '').trim();

    if (module.type === 'account') {
      ref = ref || (item.sub_title ? 'ABN ' + this.formatAbn(item.sub_title) : '');
    } else if (module.type === 'contact') {
      ref = ref || (item.bpay_reference ? 'CRN ' + item.bpay_reference : '');
      subtitle = subtitle || (item.sub_title || '').trim();
    } else {
      // "S-WEB-7402: Grange House" → ref pill + project name
      const split = title.indexOf(':');
      if (split > -1) {
        ref = ref || title.slice(0, split).trim();
        title = title.slice(split + 1).trim();
      }
      subtitle = subtitle || (item.sub_title || '').trim();
      title = title || ref;
    }

    return {
      id: +item.id,
      type: module.type,
      title,
      segments: this.highlight(title),
      subtitle,
      ref,
      link: [module.route, item.id]
    };
  }

  /** Split a title around every case-insensitive occurrence of the search term. */
  private highlight(title: string): TitleSegment[] {
    const term = this.term.trim();
    if (!term) {
      return [{text: title, hit: false}];
    }
    const segments: TitleSegment[] = [];
    const needle = term.toLowerCase();
    const haystack = title.toLowerCase();
    let from = 0;
    let at = haystack.indexOf(needle);
    while (at > -1) {
      if (at > from) {
        segments.push({text: title.slice(from, at), hit: false});
      }
      segments.push({text: title.slice(at, at + needle.length), hit: true});
      from = at + needle.length;
      at = haystack.indexOf(needle, from);
    }
    if (from < title.length) {
      segments.push({text: title.slice(from), hit: false});
    }
    return segments.length ? segments : [{text: title, hit: false}];
  }

  /** 11-digit ABNs read as "99 334 832 853". */
  private formatAbn(abn: string): string {
    const digits = String(abn).replace(/\D/g, '');
    if (digits.length !== 11) {
      return String(abn).trim();
    }
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }

  /**
   * Keep the chip that was just selected fully inside the scope row.
   *
   * The row scrolls horizontally once the chips outgrow the panel, so a chip can be selected
   * while it sits outside — or half outside — the scrollport: pick Orders on a narrow panel,
   * then pick All, and the gold pill is left clipped by the left edge. This nudges the row by
   * the smallest amount that clears the chip and does nothing at all when the chip is already
   * whole, so the row never jumps under a click that did not need it.
   *
   * The row's own padding is the gutter, which is what makes the first and last chips settle
   * back on their natural rest position rather than flush against the edge — `scrollIntoView`
   * aligns to the padding box and would eat that gutter.
   *
   * Everything here is measured in LAYOUT units (`offsetLeft` / `offsetWidth`), never in client
   * rects. `scrollLeft` is untransformed, client rects are not, and `.gs-palette` runs `pal-in`
   * — a `scale(.98)` — for 260ms after the palette opens. Mixing the two under that animation
   * under-reports the offset by a pixel or two and leaves the chip a hair clipped, which is
   * precisely the bug this method exists to prevent, only harder to see.
   *
   * Measured synchronously on purpose: `.chip.on` only repaints background and border colour,
   * so no chip changes size when the scope flips and there is nothing to wait a frame for.
   */
  private revealScope(scope: Scope): void {
    const bar = this.scopeBar?.nativeElement;
    const maxScroll = bar ? bar.scrollWidth - bar.clientWidth : 0;
    if (!bar || maxScroll <= 0) {
      return;
    }
    const chip = bar.querySelectorAll<HTMLElement>('.chip')[this.scopes.findIndex(s => s.key === scope)];
    if (!chip) {
      return;
    }
    const style = getComputedStyle(bar);
    // Where the chip starts in the row's own scroll coordinates — the same origin `scrollLeft`
    // counts from. The chips share the row's `offsetParent` while it is unpositioned, so their
    // offsets are only comparable after the row's own border is taken back off.
    const start = chip.offsetParent === bar
      ? chip.offsetLeft
      : chip.offsetLeft - bar.offsetLeft - bar.clientLeft;
    const atMost = start - parseFloat(style.paddingLeft);
    const atLeast = start + chip.offsetWidth + parseFloat(style.paddingRight) - bar.clientWidth;

    // Clamp the position we are already at into the band that shows the chip whole: `atLeast`
    // first so that a chip wider than the row still resolves to its left edge rather than its
    // right, which is the end you read a label from.
    let target = Math.min(Math.max(bar.scrollLeft, atLeast), atMost);
    target = Math.min(Math.max(target, 0), maxScroll);
    if (Math.abs(target - bar.scrollLeft) >= 1) {
      bar.scrollTo({left: target, behavior: 'smooth'});
    }
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      document.getElementById('gs-hit-' + this.activeIndex)?.scrollIntoView({block: 'nearest'});
    });
  }
}
